import React, { useState, useEffect, useMemo } from "react";
import "./OrganizedTourProfile.scss";
import "../Profiles.scss";
import { useSelectedResult } from "../../../contexts/SelectedResultContext";
import { useSearch } from "../../../contexts/SearchContext";
import {
  createLdsNewOrder,
  findExtendedDeals,
  getHotelInfo,
  updateLdsOrder,
} from "../../../services/fhpService";
import { useOrganizedTourProfileInfo } from "../../../hooks/useOrganizedTourProfileInfo";
import { decodeOrganizedTourDealId } from "../../../utils/organizedTourRoutes";
import Stages from "../../../Components/Stages/Stages";
import PackageDetails from "./PackageDetails/PackageDetails";
import PackagePayment from "./PackagePayment/PackagePayment";
import SummaryFooter from "../../../Components/InfoBox/SummaryFooter";
import ShoppingCart from "../../../Components/InfoBox/ShoppingCart";
import { getUserDetails } from "../../../services/authService";
import {
  get_depret_from_result,
  parseToLdsDate,
} from "../../../utils/LDSHelpers";
import { buildLdsOrderPersistCommissionFields } from "../../../utils/ldsOrderPersistCommission";
import { buildOrganizedTourPassengersInfoForLdsSave } from "../../../modules/organizedTours/utils/buildOrganizedTourPassengersInfoForLdsSave";
import { resolveOrganizedTourHeroImageForPersist } from "../../../utils/organizedTourPackageSuccessViewModel";
import { resolveOrganizedTourProfileDisplayIls, readOrganizedTourAgentHrPercent } from "../../../modules/organizedTours/utils/resolveOrganizedTourSearchCardPackageIls";
import { useNavigate, useParams } from "react-router-dom";
import { ordersDashboardCategoryPath } from "../../../utils/ordersDashboardRoutes";
import PackageOrderSummary from "../../../Components/Agent/SearchAgent/PackageObject/PackageOrderSummary";
// import Timer from "../../Timer/Timer";

import {
  getTotalFeesCount,
  getTotalSumPaid,
} from "../../../utils/PaymentHelpers";
import { CardsProvider } from "../../../contexts/CardsContext";
import { round2, useAppContext } from "../../../contexts/AppContext";
import { compactPricingAuditMeta } from "../../../pricing/compactPricingAuditMeta";
import { normalizePricingAuditProductType } from "../../../pricing/pricingAuditProductType";
import {
  isLdsOrderBusinessSettled,
  ldsOrderWizardShouldShowSummaryStage,
  getLdsSavedOrderValidationMessage,
  isLdsSavedOrderValidationFailed,
} from "../../../utils/ldsOrderStatus";
import { message } from "antd";

const OrganizedTourProfile = ({ orderSaved = false, ourLdsId }) => {
  const {
    currentStageIndex,
    setCurrentStageIndex,
    moveNextStage,
    selectedResult,
    fieldsFilled,
    roomsAndPaxInfo,
    ourSavedLDSOrder,
    setOurSavedLDSOrder,
    isConditionsConfirmed,
    setIsConditionsConfirmed,
    calculateLdsTotalPayment,
    ldsSupplement,
    ourPrice,
    setExtendedDealApiData,
    extendedDealApiData,
    handleFeeData,
  } = useSelectedResult();
  const { calculateLDSDealFareForRoomsComposition } = useSearch();

  const { id: our_order_id, dealId: routeDealIdParam } = useParams();

  const [loading, setLoading] = useState(false);
  const routeDealId =
    decodeOrganizedTourDealId(routeDealIdParam) || dealId || "";
  const orgTourInfo = useOrganizedTourProfileInfo(selectedResult, routeDealId);

  const mergedOurPriceForPersist = useMemo(() => {
    const base = ourPrice && typeof ourPrice === "object" ? { ...ourPrice } : {};
    const ruRaw = orgTourInfo?.resourceUrl;
    const resourceUrl =
      ruRaw != null && String(ruRaw).trim() ? String(ruRaw).trim() : null;
    const snap = resolveOrganizedTourHeroImageForPersist({
      selectedResult,
      resourceUrl,
    });
    const hasExisting = String(
      base.organized_tour_image_url || base.organizedTourImageUrl || ""
    ).trim();
    if (snap.url && !hasExisting) {
      return { ...base, organized_tour_image_url: snap.url };
    }
    return base;
  }, [ourPrice, selectedResult, orgTourInfo?.resourceUrl]);

  const navigate = useNavigate();
  const user = getUserDetails();
  const { agentEntitlements } = useAppContext();

  const { tashfees: tashFees, sumpaid } = ourSavedLDSOrder || {};
  const tashfeesCount = getTotalFeesCount(tashFees);
  const {
    ldsDealId,
    dealId,
    linkedFlights,
    supplierDetails,
    fareDetails,
    dealFare,
  } = selectedResult || {};
  const { supplierId } = supplierDetails || {};
  // const { hotelCity, hotelName, hotelId } = hotel;
  // const { ldsDealId } = linkedFlights;

  const { currency } = fareDetails || {};

  const totalSumPaid = getTotalSumPaid(sumpaid, tashfeesCount);

  const { depDate, retDate } = selectedResult
    ? get_depret_from_result(selectedResult)
    : {};

  const organizedTourCardsAuditMeta = useMemo(() => {
    const oid = ourSavedLDSOrder?.id ?? our_order_id;
    return compactPricingAuditMeta({
      refKind: oid != null && String(oid).trim() !== "" ? "booking" : undefined,
      bookingId:
        oid != null && String(oid).trim() !== "" ? String(oid) : undefined,
      productType: normalizePricingAuditProductType(
        selectedResult?.dealType ?? selectedResult?.dealtype
      ),
      supplierCode: supplierId != null ? String(supplierId) : undefined,
      agentId: user?.id != null ? String(user.id) : undefined,
      agentPlan:
        agentEntitlements?.package != null
          ? String(agentEntitlements.package)
          : undefined,
      channelType: "agent_site",
      pricingModel: "organizedTourProfile:CardsProvider",
    });
  }, [
    ourSavedLDSOrder?.id,
    our_order_id,
    selectedResult?.dealType,
    selectedResult?.dealtype,
    supplierId,
    user?.id,
    agentEntitlements?.package,
  ]);

  const insertLdsNewOrder = (later) => {
    createCustomerPriceQuote({ moveNextStateFlag: true, later });
  };

  const createCustomerPriceQuote = async ({
    moveNextStateFlag = false,
    later,
  }) => {
    setLoading(true);
    try {
      const { id: agentUserId } = user;
      const pkgLds =
        linkedFlights?.ldsDealId ??
        linkedFlights?.lds_deal_id ??
        ldsDealId ??
        "";
      const orgTourPaxBuild = buildOrganizedTourPassengersInfoForLdsSave(
        roomsAndPaxInfo,
        extendedDealApiData,
        pkgLds
      );
      if (!orgTourPaxBuild.allRoomsHaveRoomSet) {
        message.error(
          "חסרה הקצאת סוג חדר לנוסעים. נסו לרענן את הדף או לחכות לטעינת מחירי החדרים."
        );
        return null;
      }
      const persistComm = buildLdsOrderPersistCommissionFields(ourPrice);
      const body = {
        agent_id: agentUserId,
        later: later ? "true" : "false",
        commission: persistComm.commission,
        handling_fee: persistComm.handling_fee,
        currency: "USD",
        deal_id: dealId,
        deal_type: "OrganizedTour",
        supplierId: supplierId,
        passengers_info: orgTourPaxBuild.passengersInfo,
        ret_date: parseToLdsDate(retDate),
        dep_date: parseToLdsDate(depDate),
        supplement: ldsSupplement,
        ourPrice: mergedOurPriceForPersist,
        linkedFlights: linkedFlights,
        extended: extendedDealApiData,
      };
      if (ourLdsId) {
        body.ourLdsId = ourLdsId;
        const res = await updateLdsOrder(body);
        const data = res?.data;
        if (data) {
          if (isLdsSavedOrderValidationFailed(data)) {
            message.error(
              getLdsSavedOrderValidationMessage(data) ||
                "לא ניתן להשלים את השמירה. אנא בדקו את הפרטים."
            );
            return null;
          }
          setOurSavedLDSOrder(data);
        }
        savePriceOfferLink(window.location.href, "OrganizedTour");
        return data?.id ?? ourLdsId ?? null;
      }
      const res = await createLdsNewOrder(body);
      const newId = res?.data?.id;
      if (res?.data) {
        if (isLdsSavedOrderValidationFailed(res.data)) {
          message.error(
            getLdsSavedOrderValidationMessage(res.data) ||
              "לא ניתן ליצור הזמנה. אנא בדקו את הפרטים."
          );
          return null;
        }
        setOurSavedLDSOrder(res?.data);
      }
      setTimeout(() => {
        savePriceOfferLink(window.location.href, "OrganizedTour");
      }, 100);
      if (!later) {
        if (newId) navigate(`${newId}`);
        if (moveNextStateFlag) moveNextStage();
      }
      return newId ?? null;
    } finally {
      setLoading(false);
    }
  };

  // Function to save price offer link to recent quotes
  const savePriceOfferLink = (link, dealType) => {
    try {
      // Get existing recent quotes from localStorage
      const existingQuotes = JSON.parse(localStorage.getItem('recentPriceOffers') || '[]');
      
      // Create new quote object with enhanced data
      const newQuote = {
        id: `price_offer_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
        type: 'price_offer',
        link: link,
        deal_type: dealType,
        created_at: new Date().toISOString(),
        agent: user,
        status: 'active',
        title: dealType === 'FlightsHotel' ? 'הצעת חבילה' : 'הצעת טיול מאורגן',
        description: `הצעת מחיר שנוצרה על ידי ${user?.fullname || user?.firstname + ' ' + user?.lastname || 'סוכן'}`,
        // Add additional data for better display
        tour: selectedResult,
        ourPrice: ourPrice,
        passengers_info: roomsAndPaxInfo,
        dep_date: depDate,
        ret_date: retDate,
        dealId: dealId
      };

      // Add to beginning of array (most recent first)
      const updatedQuotes = [newQuote, ...existingQuotes.slice(0, 19)]; // Keep max 20 quotes
      
      // Save back to localStorage
      localStorage.setItem('recentPriceOffers', JSON.stringify(updatedQuotes));
      
      console.log("✅ Price offer link saved to recent quotes:", newQuote);
    } catch (error) {
      console.error('Error saving price offer link:', error);
    }
  };

  const profilePackageIls = resolveOrganizedTourProfileDisplayIls({
    ourPrice,
    selectedResult,
    calculateLDSDealFareForRoomsComposition,
    agentHrPercent: readOrganizedTourAgentHrPercent(agentEntitlements),
  });
  const leftToPay =
    profilePackageIls != null
      ? round2(Number(profilePackageIls) - totalSumPaid)
      : null;

  useEffect(() => {
    calculateLdsTotalPayment();
  }, [selectedResult]);

  useEffect(() => {
    if (!ldsDealId) return;
    _get_rooms_info();
  }, [ldsDealId]);

  function _get_rooms_info() {
    findExtendedDeals({
      lds_deal_id: ldsDealId,
      deal_type: "OrganizedTour",
    })
      .then((res) => {
        let extendedDeal = res?.data?.extendedDeal;
        if (extendedDeal && !Array.isArray(extendedDeal)) {
          extendedDeal = [extendedDeal];
        }
        setExtendedDealApiData(extendedDeal);
      })
      .catch((err) => console.log({ err }));
  }

  const stages = [
    {
      title: "פרטי הטיול",
      disabled: false,
      component: (
        <PackageDetails
          disabled={orderSaved}
          createCustomerPriceQuote={createCustomerPriceQuote}
          leftToPay={leftToPay}
          orgTourInfo={orgTourInfo}
          orderId={ourLdsId || our_order_id || ourSavedLDSOrder?.id || ourSavedLDSOrder?.ourLdsId}
        />
      ),
    },
    {
      title: "ביצוע תשלום",
      disabled: true,
      component: (
        <CardsProvider
          totalPrice={leftToPay}
          pricingAuditMeta={organizedTourCardsAuditMeta}
        >
          <PackagePayment
            our_order_id={our_order_id}
            leftToPay={leftToPay}
            sumpaid={totalSumPaid}
            disabled={orderSaved}
            isConditionsConfirmed={isConditionsConfirmed}
            setIsConditionsConfirmed={setIsConditionsConfirmed}
          />
        </CardsProvider>
      ),
    },
    {
      title: "אישור וסיכום הזמנה",
      disabled: true,
      component: <PackageOrderSummary />,
    },
  ];

  useEffect(() => {
    if (ldsOrderWizardShouldShowSummaryStage(ourSavedLDSOrder)) {
      setCurrentStageIndex(2);
    }
  }, [ourSavedLDSOrder, setCurrentStageIndex]);

  const handleMoveNextStep = (text) => {
    switch (currentStageIndex) {
      case 0:
        if (fieldsFilled) {
          insertLdsNewOrder(text === "later");
          moveNextStage();
        }
        break;
      case 1:
        if (orderSaved) {
          if (isLdsOrderBusinessSettled(ourSavedLDSOrder)) moveNextStage();
        }
        break;
      case 2: {
        const row = ourSavedLDSOrder;
        const hasLdsPayload = row?.order_response != null;
        if (isLdsOrderBusinessSettled(row) || hasLdsPayload) {
          navigate(ordersDashboardCategoryPath("organized-tours"));
        }
        break;
      }
      default:
        break;
    }
  };

  const isNextStageDisabled = () => {
    // return false;

    if (!isConditionsConfirmed) {
      return "יש לאשר את התקנון";
    }
    if (currentStageIndex === 0) {
      // Allow creating price offer even without filling all fields
      // Only require basic fields to be filled
      if (!fieldsFilled) {
        return "יש למלא את השדות הבסיסיים";
      }
    } else if (currentStageIndex === 1) {
      if (!isLdsOrderBusinessSettled(ourSavedLDSOrder)) {
        return "יש לשלם עבור כל ההזמנה";
      }
    }
    // else if (currentStageIndex === 2) {
    // }
    return false;
  };

  return (
    <div className="packages-profile-wrapper organized-tour-profile-shell">
      <div className="packages-profile organized-tour-profile-v2">
        <Stages
          stages={stages}
          currentIndex={currentStageIndex}
          setCurrentStageIndex={setCurrentStageIndex}
          titleStyle={{ fontSize: 18, fontWeight: 600 }}
          lineStyle={{ margin: "0 16px" }}
          className="organized-tour-stages"
        />
        <div className="packages-profile-inner">
          {stages[currentStageIndex].component}
        </div>
        <div id="org-tour-summary-footer">
          <SummaryFooter
            nextStageDisabled={isNextStageDisabled()}
            moveNextStage={handleMoveNextStep}
            beforePayment={currentStageIndex === 0}
            leftToPay={leftToPay}
            loading={loading}
          />
        </div>
      </div>
    </div>
  );
};

export default OrganizedTourProfile;
